import { c as _c } from "react/compiler-runtime";
import { postHostedUserDocumentsByIdProcessMutation } from '@onefootprint/fetch-hosted';
import { useRequestError } from '@onefootprint/fetch-hosted/use-request-error';
import type { DocumentResponse } from '@onefootprint/request-types';
import { useMutation } from '@tanstack/react-query';
import upperFirst from 'lodash/upperFirst';
import { useEffect, useRef, useState } from 'react';
import { getLogger, trackAction } from '@/idv2/utils/logger';
import { SLOW_CONNECTION_MESSAGE_TIMEOUT } from '../../../constants';
import useSubmitDoc from '../../../hooks/use-submit-doc';
import { IdDocImageUploadError } from '../../../types';
import { bytesToMegabytes } from '../../../utils/capture';
import getBackendErrorMessage from '../../../utils/get-backend-error-message';
import getRejectionCodes from '../../../utils/get-rejection-codes';
import { useNonIdDocMachine } from '../../context/non-id-doc-context';
import useDocName from '../../use-doc-name';
import DesktopProcessingView from './desktop-processing-view';
const {
  logWarn,
  logInfo
} = getLogger({
  name: 'idv2',
  location: 'non-id-doc-desktop-processing'
});
const DesktopProcessing = () => {
  const $ = _c(49);
  const {
    getErrorMessage,
    getErrorCode
  } = useRequestError();
  const [state, dispatch] = useNonIdDocMachine();
  const submitDocMutation = useSubmitDoc();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = postHostedUserDocumentsByIdProcessMutation();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const processDocMutation = useMutation(t0);
  const [mode, setMode] = useState("loading");
  const [showSlowConnectionMessage, setShowSlowConnectionMessage] = useState(false);
  const [retryLimitExceeded, setRetryLimitExceeded] = useState(false);
  const [isMissingRequirements, setIsMissingRequirements] = useState(false);
  const [step, setStep] = useState("upload");
  const slowConnectionTimer = useRef(null);
  const {
    document,
    authToken,
    id,
    requirement
  } = state.context;
  const {
    kind: documentRequestKind
  } = requirement.config;
  const t1 = useDocName(requirement.config);
  let t2;
  if ($[1] !== t1) {
    t2 = upperFirst(t1);
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const documentName = t2;
  let t3;
  if ($[3] !== documentRequestKind) {
    t3 = () => {
      trackAction(`document-processing-${documentRequestKind}:started`);
    };
    $[3] = documentRequestKind;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== dispatch || $[7] !== documentRequestKind || $[8] !== id) {
    t5 = data => {
      const {
        errorDetails,
        nextSideToCollect,
        isRetryLimitExceeded
      } = data;
      const rejectionCodes = getRejectionCodes(data);
      const rejectionContext = {
        rejectionCodes,
        docKind: documentRequestKind
      };
      if (isRetryLimitExceeded) {
        logWarn(`Image upload retry limit exceeded. doc: ${documentRequestKind}, doc id: ${id}`, undefined, rejectionContext);
        trackAction(`document-processing-${documentRequestKind}:retry-limit-exceeded`, rejectionContext);
        setRetryLimitExceeded(true);
        return;
      }
      if (nextSideToCollect === "front") {
        logWarn("Document rejected", undefined, rejectionContext);
        trackAction(`document-processing-${documentRequestKind}:rejected`, rejectionContext);
        dispatch({
          type: "processingErrored",
          payload: {
            errors: errorDetails.map(_temp)
          }
        });
        return;
      }
      if (nextSideToCollect) {
        const unexpectedContext = {
          ...rejectionContext,
          nextSide: nextSideToCollect
        };
        logWarn(`Unexpected next side to collect flow. Side: ${nextSideToCollect}`, undefined, unexpectedContext);
        trackAction(`document-processing-${documentRequestKind}:unexpected-next-side`, unexpectedContext);
        return;
      }
      setMode("success");
      trackAction(`document-processing-${documentRequestKind}:completed`, rejectionContext);
    };
    $[6] = dispatch;
    $[7] = documentRequestKind;
    $[8] = id;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleProcessDocSuccess = t5;
  let t6;
  if ($[10] !== dispatch) {
    t6 = err_0 => {
      const backendMessage = getBackendErrorMessage(err_0);
      dispatch({
        type: "processingErrored",
        payload: {
          errors: [backendMessage ? {
            errorType: "custom_error",
            errorInfo: backendMessage
          } : {
            errorType: IdDocImageUploadError.unknownUploadError
          }]
        }
      });
    };
    $[10] = dispatch;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleError = t6;
  let t7;
  if ($[12] !== getErrorMessage || $[13] !== handleError || $[14] !== id) {
    t7 = err_1 => {
      handleError(err_1);
      logWarn(`Error while processing image ${id}: ${getErrorMessage(err_1)}`, err_1);
    };
    $[12] = getErrorMessage;
    $[13] = handleError;
    $[14] = id;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleProcessDocError = t7;
  let t8;
  if ($[16] !== documentRequestKind || $[17] !== getErrorCode || $[18] !== getErrorMessage || $[19] !== handleError || $[20] !== id) {
    t8 = err_2 => {
      const errorCode = getErrorCode(err_2);
      const errorMessage = getErrorMessage(err_2);
      if (errorCode === "E109") {
        setMode("success");
        return;
      }
      handleError(err_2);
      logWarn(`Submit failed on phone flow. Document kind: ${documentRequestKind}, upload session id: ${id}. Error: ${errorMessage}`);
    };
    $[16] = documentRequestKind;
    $[17] = getErrorCode;
    $[18] = getErrorMessage;
    $[19] = handleError;
    $[20] = id;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const handleSubmitDocError = t8;
  let t9;
  if ($[22] !== authToken || $[23] !== handleProcessDocError || $[24] !== handleProcessDocSuccess || $[25] !== id || $[26] !== processDocMutation) {
    t9 = async () => {
      if (!id) {
        return;
      }
      setStep("analyze");
      ;
      try {
        const response = await processDocMutation.mutateAsync({
          path: {
            id
          },
          headers: {
            "X-Fp-Authorization": authToken
          }
        });
        handleProcessDocSuccess(response);
      } catch (t10) {
        const error = t10;
        handleProcessDocError(error);
      }
    };
    $[22] = authToken;
    $[23] = handleProcessDocError;
    $[24] = handleProcessDocSuccess;
    $[25] = id;
    $[26] = processDocMutation;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const handleSubmitDocSuccess = t9;
  let t10;
  if ($[28] !== authToken || $[29] !== document || $[30] !== handleSubmitDocError || $[31] !== handleSubmitDocSuccess || $[32] !== id || $[33] !== submitDocMutation) {
    t10 = () => {
      if (!document || !authToken || !id) {
        setIsMissingRequirements(true);
        const error_0 = `Desktop web flow - id-doc image could not be processed due to missing requirements. Requirements - document: ${document ? "OK" : "undefined"}, auth token: ${authToken ? "OK" : "undefined"}, id: ${id ? "OK" : "undefined"}`;
        logWarn(error_0);
        return;
      }
      if (!slowConnectionTimer.current) {
        slowConnectionTimer.current = setTimeout(() => {
          if (submitDocMutation.isSuccess) {
            return;
          }
          setShowSlowConnectionMessage(true);
        }, SLOW_CONNECTION_MESSAGE_TIMEOUT);
      }
      const {
        imageFile,
        extraCompressed,
        captureKind
      } = document;
      logInfo(`Uploading image with size ${bytesToMegabytes(imageFile.size)} MB and type ${imageFile.type} in POST request`);
      submitDocMutation.mutate({
        body: {
          fileData: imageFile
        },
        path: {
          id,
          side: "front"
        },
        headers: {
          "X-Fp-Authorization": authToken,
          "X-Fp-Is-Extra-Compressed": String(extraCompressed),
          "X-Fp-Is-Manual": String(captureKind === "manual"),
          "X-Fp-Is-App-Clip": "false",
          "X-Fp-Is-Upload": String(captureKind === "upload"),
          "X-Fp-Is-Forced-Upload": "false"
        }
      }, {
        onSuccess: handleSubmitDocSuccess,
        onError: handleSubmitDocError,
        onSettled: () => {
          if (slowConnectionTimer.current) {
            clearTimeout(slowConnectionTimer.current);
            setShowSlowConnectionMessage(false);
          }
        }
      });
    };
    $[28] = authToken;
    $[29] = document;
    $[30] = handleSubmitDocError;
    $[31] = handleSubmitDocSuccess;
    $[32] = id;
    $[33] = submitDocMutation;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = [];
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  useEffect(t10, t11);
  let t12;
  if ($[36] !== dispatch) {
    t12 = () => dispatch({
      type: "retryLimitExceeded"
    });
    $[36] = dispatch;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const handleRetryLimitExceeded = t12;
  let t13;
  if ($[38] !== dispatch) {
    t13 = () => dispatch({
      type: "processingSucceeded"
    });
    $[38] = dispatch;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  const handleSuccess = t13;
  let t14;
  if ($[40] !== documentName || $[41] !== handleRetryLimitExceeded || $[42] !== handleSuccess || $[43] !== isMissingRequirements || $[44] !== mode || $[45] !== retryLimitExceeded || $[46] !== showSlowConnectionMessage || $[47] !== step) {
    t14 = <DesktopProcessingView mode={mode} step={step} showSlowConnectionMessage={showSlowConnectionMessage} retryLimitExceeded={retryLimitExceeded} isMissingRequirements={isMissingRequirements} documentName={documentName} onRetryLimitExceeded={handleRetryLimitExceeded} onSuccess={handleSuccess} />;
    $[40] = documentName;
    $[41] = handleRetryLimitExceeded;
    $[42] = handleSuccess;
    $[43] = isMissingRequirements;
    $[44] = mode;
    $[45] = retryLimitExceeded;
    $[46] = showSlowConnectionMessage;
    $[47] = step;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  return t14;
};
export default DesktopProcessing;
function _temp(err) {
  return {
    errorType: err.code ?? "custom_error",
    errorInfo: err.message
  };
}